import React, { useState, useEffect, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { MONACO_LANGUAGE, codingLanguageLabel, isReviewLanguage } from '../utils/codingLanguages';
import { ProctoringMonitor } from '../components/proctoring/ProctoringMonitor';
import VideoRecorderWithMixedAudio from '../utils/VideoRecorderWithMixedAudio';
import { claimInterviewResources, getClaimedInterviewResources, isStreamLive, peekContinuousVideoRecorder, clearStashedVideoRecorder } from '../utils/interviewPrep';
import { fetchQuestion, runCode, submitCode, saveDraft, submitExam } from '../services/codingApi';
import { useBrowserSecurity } from '../hooks/useBrowserSecurity';
import {
  isFullscreenActive,
  requestExamFullscreen,
  addFullscreenChangeListener,
  requestEntireScreenShare,
} from '../utils/fullscreen';
import { getPublicApiOrigin } from '../config/env';
import './CodingExamRoom.css';

const PROBLEM_SECTIONS = ['PROBLEM STATEMENT', 'INPUT FORMAT', 'OUTPUT FORMAT', 'CONSTRAINTS', 'REQUIREMENTS', 'EXAMPLES'];

function sectionBody(text, title) {
  const match = String(text || '').match(new RegExp(`${title}:\\s*([\\s\\S]*?)(?=\\n(?:Input|Output|Explanation):|$)`, 'i'));
  return match ? match[1].trim() : '';
}

function parseExamples(block) {
  if (!block) return [];
  return block.split(/\n(?=Example\s+\d+)/i).map((chunk) => chunk.trim()).filter(Boolean).map((chunk) => ({
    input: sectionBody(chunk, 'Input'),
    output: sectionBody(chunk, 'Output'),
    explanation: sectionBody(chunk, 'Explanation'),
  })).filter((example) => example.input || example.output || example.explanation);
}

function parseCodingDescription(description) {
  const text = String(description || '').replace(/\r\n/g, '\n');
  const lines = text.split('\n');
  const marks = [];
  lines.forEach((line, index) => {
    const title = line.trim().toUpperCase();
    if (PROBLEM_SECTIONS.includes(title)) marks.push({ title, index });
  });
  if (!marks.length) {
    return { problemStatement: text.trim(), inputFormat: '', outputFormat: '', constraints: '', requirements: '', examples: [] };
  }
  const sections = {};
  marks.forEach((mark, markIndex) => {
    const end = marks[markIndex + 1] ? marks[markIndex + 1].index : lines.length;
    sections[mark.title] = lines.slice(mark.index + 1, end).join('\n').trim();
  });
  return {
    problemStatement: sections['PROBLEM STATEMENT'] || lines.slice(0, marks[0].index).join('\n').trim(),
    inputFormat: sections['INPUT FORMAT'] || '',
    outputFormat: sections['OUTPUT FORMAT'] || '',
    constraints: sections['CONSTRAINTS'] || '',
    requirements: sections['REQUIREMENTS'] || '',
    examples: parseExamples(sections.EXAMPLES || ''),
  };
}

const WARNING_THRESHOLD_SECONDS = 5 * 60;
const AUTOSAVE_INTERVAL_MS = 10000;

function formatSeconds(total) {
  if (total == null || total < 0) total = 0;
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function ProblemSection({ title, children }) {
  if (!children) return null;
  return (
    <section className="qpanel-section">
      <h4 className="qpanel-section-title">{title}</h4>
      <div className="qpanel-section-body">{children}</div>
    </section>
  );
}

function CodingProblemPanel({ question, questionNumber, language }) {
  const parsed = parseCodingDescription(question.description);
  const samples = question.visible_tests || [];
  const inputLabel = language === 'sql' ? 'Setup SQL' : 'Input';
  const outputLabel = language === 'sql' ? 'Expected rows' : 'Output';

  return (
    <article className="question-panel">
      <header className="qpanel-header">
        <div className="qpanel-header-row">
          <span className="qpanel-qnum">Q{questionNumber}</span>
          <h2 className="qpanel-title">{question.title}</h2>
        </div>
      </header>

      <ProblemSection title="Problem Statement">
        {parsed.problemStatement ? <p className="qpanel-prose">{parsed.problemStatement}</p> : null}
      </ProblemSection>
      <ProblemSection title="Input Format">
        {parsed.inputFormat ? <pre className="qpanel-mono">{parsed.inputFormat}</pre> : null}
      </ProblemSection>
      <ProblemSection title="Output Format">
        {parsed.outputFormat ? <pre className="qpanel-mono">{parsed.outputFormat}</pre> : null}
      </ProblemSection>
      <ProblemSection title="Constraints">
        {parsed.constraints ? <pre className="qpanel-mono">{parsed.constraints}</pre> : null}
      </ProblemSection>
      <ProblemSection title="Requirements">
        {parsed.requirements ? <p className="qpanel-prose">{parsed.requirements}</p> : null}
      </ProblemSection>

      {parsed.examples.length > 0 && (
        <ProblemSection title="Examples">
          {parsed.examples.map((example, exampleIndex) => (
            <div key={`example-${exampleIndex}`} className="qpanel-example-card">
              <div className="qpanel-example-label">Example {exampleIndex + 1}</div>
              <div className="qpanel-io-label">{inputLabel}</div>
              <pre className="qpanel-mono">{example.input || '—'}</pre>
              <div className="qpanel-io-label">{outputLabel}</div>
              <pre className="qpanel-mono">{example.output || '—'}</pre>
              {example.explanation ? (
                <div className="qpanel-explanation">
                  <div className="qpanel-io-label">Explanation</div>
                  <p>{example.explanation}</p>
                </div>
              ) : null}
            </div>
          ))}
        </ProblemSection>
      )}

      {samples.length > 0 && (
        <ProblemSection title="Sample Test Cases">
          {samples.map((test, testIndex) => (
            <div key={test.id || `sample-${testIndex}`} className="qpanel-sample-card">
              <div className="qpanel-example-label">Test Case {testIndex + 1}</div>
              <div className="qpanel-io-label">{inputLabel}</div>
              <pre className="qpanel-mono">{test.input || '—'}</pre>
              <div className="qpanel-io-label">{outputLabel}</div>
              <pre className="qpanel-mono">{test.expected || '—'}</pre>
            </div>
          ))}
        </ProblemSection>
      )}
    </article>
  );
}

const CodingExamRoom = ({
  sessionIdFromLanding,
  prepResources: prepFromParent,
  isResuming = false,
  resumeState,
  fullscreenRequired: _fullscreenRequiredProp = true,
}) => {
  // Coding exams always require fullscreen for integrity (campaign flag is advisory only).
  const fullscreenRequired = true;
  const sessionId = sessionIdFromLanding;
  const prepResourcesRef = useRef(
    prepFromParent || getClaimedInterviewResources() || claimInterviewResources()
  );
  const prepResources = prepResourcesRef.current;
  const hasLivePrewarm = isStreamLive(prepResources?.stream);

  const [index, setIndex] = useState(resumeState?.currentQuestionIndex ?? 0);
  const [total, setTotal] = useState(1);
  const [question, setQuestion] = useState(null);
  const [language, setLanguage] = useState(resumeState?.language || null);
  const codeCacheRef = useRef({});
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [running, setRunning] = useState(false);
  const [runResults, setRunResults] = useState(null);
  const [customInput, setCustomInput] = useState('');
  const [compileError, setCompileError] = useState(null);
  const [executionBanner, setExecutionBanner] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showSubmitExamConfirm, setShowSubmitExamConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [examComplete, setExamComplete] = useState(false);
  // Offline mid-exam: drafts and recording slices wait and retry; tell the candidate to stay.
  const [browserOnline, setBrowserOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  );
  useEffect(() => {
    const on = () => setBrowserOnline(true);
    const off = () => setBrowserOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const [endsAt, setEndsAt] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const videoRecorderRef = useRef(null);
  const videoElementRef = useRef(null);
  const recorderInitializedRef = useRef(false);
  const recorderInitializingRef = useRef(false);
  const recordingStartedRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  const finishingRef = useRef(false);
  const examCompleteRef = useRef(false);
  const submitLockRef = useRef(false);

  // Adopt continuous AI→coding MediaRecorder when present (one SessionVideo for whole exam).
  useEffect(() => {
    const handed = prepResources?.videoRecorder || peekContinuousVideoRecorder();
    if (!handed) return;
    videoRecorderRef.current = handed;
    recorderInitializedRef.current = true;
    // Keep the continuous recording's screen phase on this room's live share (if it has one).
    if (screenStreamRef.current) handed.setScreenStream?.(screenStreamRef.current);
    if (handed.isRecording) {
      recordingStartedRef.current = true;
      const start = handed.getRecordingStartTime?.() || prepResources?.recordingStartTime || null;
      if (start) setRecordingStartTime(start);
    }
    clearStashedVideoRecorder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showFullscreenGate, setShowFullscreenGate] = useState(
    !!fullscreenRequired && !isFullscreenActive()
  );
  const [showScreenShareGate, setShowScreenShareGate] = useState(
    !isStreamLive(prepResources?.screenStream)
  );
  const [securityNotice, setSecurityNotice] = useState('');
  const screenStreamRef = useRef(prepResources?.screenStream || null);

  const currentQuestionIdRef = useRef(null);

  useEffect(() => {
    examCompleteRef.current = examComplete;
  }, [examComplete]);

  const reportProctoringViolation = useCallback(async (eventType, message, severity = 'medium') => {
    const token = localStorage.getItem('access_token');
    if (!token || !sessionId) return;
    try {
      await fetch(`${getPublicApiOrigin()}/api/proctoring/${sessionId}/event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          event_type: eventType,
          severity,
          metadata: {
            message,
            videoOffsetSeconds: recordingStartTime
              ? parseFloat(((Date.now() - recordingStartTime) / 1000).toFixed(3))
              : null,
          },
        }),
      });
    } catch (e) {
      console.warn('Failed to report proctoring event:', e);
    }
  }, [sessionId, recordingStartTime]);

  const handleEnterFullscreen = useCallback(async () => {
    const ok = await requestExamFullscreen(document.documentElement);
    if (ok || isFullscreenActive()) {
      setShowFullscreenGate(false);
      return true;
    }
    setShowFullscreenGate(true);
    return false;
  }, []);

  const handleRestoreScreenShare = useCallback(async () => {
    try {
      const displayStream = await requestEntireScreenShare();
      screenStreamRef.current = displayStream;
      // Recording shows the screen after the intro minute — keep it on the live share.
      videoRecorderRef.current?.setScreenStream?.(displayStream);
      setShowScreenShareGate(false);
      const track = displayStream.getVideoTracks()[0];
      if (track) {
        track.onended = () => {
          screenStreamRef.current = null;
          setShowScreenShareGate(true);
          if (!examCompleteRef.current) {
            void reportProctoringViolation(
              'screen_share_stopped',
              'Screen sharing was stopped',
              'critical'
            );
          }
        };
      }
      return true;
    } catch (err) {
      const msg = err?.code === 'NOT_ENTIRE_SCREEN'
        ? err.message
        : 'Screen sharing is required to continue the exam.';
      setSecurityNotice(msg);
      void reportProctoringViolation('screen_share_denied', msg, 'critical');
      return false;
    }
  }, [reportProctoringViolation]);

  const handleBrowserViolation = useCallback((violation) => {
    if (examCompleteRef.current) return;
    if (violation?.type === 'fullscreen_exit') {
      setShowFullscreenGate(true);
    }
    void reportProctoringViolation(
      violation.type,
      violation.message || violation.type,
      violation.severity || 'medium'
    );
    if (violation?.message) {
      setSecurityNotice(violation.message);
      setTimeout(() => setSecurityNotice(''), 3500);
    }
  }, [reportProctoringViolation]);

  useBrowserSecurity(handleBrowserViolation, {
    enabled: !examComplete,
    blockCopyPaste: false,
    onRestoreFullscreen: fullscreenRequired
      ? () => {
          setShowFullscreenGate(true);
          void handleEnterFullscreen();
        }
      : undefined,
  });

  useEffect(() => {
    if (!fullscreenRequired) return undefined;
    // If System Check already entered fullscreen on Start, stay in it.
    if (isFullscreenActive()) {
      setShowFullscreenGate(false);
    } else {
      // Best-effort re-request (may need the gate button if gesture is gone).
      void handleEnterFullscreen();
    }
    const sync = () => {
      const active = isFullscreenActive();
      if (active) setShowFullscreenGate(false);
      else if (!examCompleteRef.current) setShowFullscreenGate(true);
    };
    return addFullscreenChangeListener(sync);
  }, [fullscreenRequired, handleEnterFullscreen]);

  useEffect(() => {
    const existing = screenStreamRef.current;
    if (!isStreamLive(existing)) return undefined;
    const track = existing.getVideoTracks?.()[0];
    if (!track) return undefined;
    const onEnded = () => {
      screenStreamRef.current = null;
      setShowScreenShareGate(true);
      if (!examCompleteRef.current) {
        void reportProctoringViolation(
          'screen_share_stopped',
          'Screen sharing was stopped',
          'critical'
        );
      }
    };
    track.addEventListener('ended', onEnded);
    return () => track.removeEventListener('ended', onEnded);
  }, [reportProctoringViolation]);

  useEffect(() => {
    return () => {
      // Do not stop the handed-off screen stream on unmount of this effect —
      // stopRecording / releaseClaimedInterviewResources owns lifecycle.
    };
  }, []);

  const handleVideoReady = useCallback((videoElement) => {
    if (!sessionId || !videoElement?.srcObject) return;
    const stream = videoElement.srcObject;
    if (!stream.active || stream.getVideoTracks().length === 0) return;

    if (!videoElementRef.current) {
      videoElementRef.current = document.createElement('video');
      videoElementRef.current.autoplay = true;
      videoElementRef.current.playsInline = true;
      videoElementRef.current.muted = true;
      videoElementRef.current.style.display = 'none';
      document.body.appendChild(videoElementRef.current);
    }
    videoElementRef.current.srcObject = stream;

    // Reuse continuous recorder from AI phase — do not create a second upload.
    if (videoRecorderRef.current || recorderInitializedRef.current) {
      if (videoRecorderRef.current?.isRecording && !recordingStartedRef.current) {
        recordingStartedRef.current = true;
        const start = videoRecorderRef.current.getRecordingStartTime?.();
        if (start) setRecordingStartTime(start);
      }
      return;
    }

    (async () => {
      if (videoRecorderRef.current || recorderInitializedRef.current || recorderInitializingRef.current) return;
      recorderInitializingRef.current = true;
      const recorder = new VideoRecorderWithMixedAudio(sessionId);
      videoRecorderRef.current = recorder;
      try {
        await recorder.initialize(videoElementRef.current, null, { screenStream: screenStreamRef.current });
        recorderInitializedRef.current = true;
      } catch (err) {
        console.error('Failed to initialize coding-exam video recorder:', err);
        videoRecorderRef.current = null;
        recorderInitializedRef.current = false;
      } finally {
        recorderInitializingRef.current = false;
      }
    })();
  }, [sessionId]);

  useEffect(() => {
    return () => {
      const rec = videoRecorderRef.current;
      if (rec) {
        if (typeof rec.stopAsync === 'function') void rec.stopAsync().catch(() => {});
        else rec.stop();
      }
      if (videoElementRef.current?.parentNode) {
        videoElementRef.current.parentNode.removeChild(videoElementRef.current);
      }
    };
  }, []);

  const ensureRecordingStarted = useCallback(() => {
    if (recordingStartedRef.current) return;
    const rec = videoRecorderRef.current;
    if (rec && !rec.isRecording) {
      rec.start();
      recordingStartedRef.current = true;
      setRecordingStartTime(rec.getRecordingStartTime());
    }
  }, []);

  const stopRecording = useCallback(async () => {
    const rec = videoRecorderRef.current;
    if (rec && recordingStartedRef.current) {
      try { await rec.stopAsync(); } catch (e) { /* best effort */ }
    }
    screenStreamRef.current?.getTracks?.().forEach((t) => t.stop());
    screenStreamRef.current = null;
  }, []);

  const loadQuestion = useCallback(async (i) => {
    setLoading(true);
    setLoadError(null);
    setRunResults(null);
    setCompileError(null);
    setExecutionBanner(null);
    try {
      const q = await fetchQuestion(i);
      setQuestion(q);
      setTotal(q.total);
      setEndsAt(q.ends_at);
      currentQuestionIdRef.current = q.question_id;

      const cached = codeCacheRef.current[q.question_id];
      let lang = language && q.languages.includes(language) ? language : q.languages[0];
      let initialCode;
      if (i === (resumeState?.currentQuestionIndex ?? -1) && resumeState?.draftCode) {
        initialCode = resumeState.draftCode;
        lang = resumeState.language || lang;
      } else if (cached?.[lang] != null) {
        initialCode = cached[lang];
      } else {
        initialCode = q.starter_code?.[lang] || '';
      }
      setLanguage(lang);
      setCode(initialCode);
      codeCacheRef.current[q.question_id] = { ...(cached || {}), [lang]: initialCode };

      ensureRecordingStarted();
    } catch (err) {
      const status = err.response?.status;
      // Server already auto-finished on deadline (410) or session is done (409).
      if (status === 410 || status === 409) {
        setExamComplete(true);
        await stopRecording();
        return;
      }
      setLoadError(err.response?.data?.detail || 'Could not load this question.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ensureRecordingStarted, stopRecording]);

  useEffect(() => {
    loadQuestion(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const handleLanguageChange = (newLang) => {
    if (!question) return;
    const qid = question.question_id;
    const cache = codeCacheRef.current[qid] || {};
    cache[language] = code;
    const nextCode = cache[newLang] != null ? cache[newLang] : (question.starter_code?.[newLang] || '');
    cache[newLang] = nextCode;
    codeCacheRef.current[qid] = cache;
    setLanguage(newLang);
    setCode(nextCode);
  };

  const handleCodeChange = (value) => {
    setCode(value ?? '');
    if (question) {
      const qid = question.question_id;
      codeCacheRef.current[qid] = { ...(codeCacheRef.current[qid] || {}), [language]: value ?? '' };
    }
  };

  const doAutosave = useCallback(async () => {
    if (!question || examComplete) return;
    await saveDraft({ questionId: question.question_id, language, code });
  }, [question, language, code, examComplete]);

  useEffect(() => {
    autosaveTimerRef.current = setInterval(doAutosave, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(autosaveTimerRef.current);
  }, [doAutosave]);

  useEffect(() => {
    const onBlur = () => doAutosave();
    window.addEventListener('blur', onBlur);
    return () => window.removeEventListener('blur', onBlur);
  }, [doAutosave]);

  useEffect(() => {
    if (!endsAt) return;
    const tick = () => {
      const remaining = Math.round((new Date(endsAt).getTime() - Date.now()) / 1000);
      setSecondsLeft(remaining);
      if (remaining <= 0 && !finishingRef.current && !examComplete) {
        finishingRef.current = true;
        handleAutoSubmitOnTimeout();
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endsAt, examComplete]);

  const rememberCurrentCode = () => {
    if (!question) return;
    codeCacheRef.current[question.question_id] = {
      ...(codeCacheRef.current[question.question_id] || {}),
      [language]: code,
    };
  };

  const handleReset = () => {
    if (!question) return;
    const starter = question.starter_code?.[language] || '';
    setCode(starter);
    codeCacheRef.current[question.question_id] = {
      ...(codeCacheRef.current[question.question_id] || {}),
      [language]: starter,
    };
  };

  const handleBack = () => {
    if (index <= 0 || submitting) return;
    rememberCurrentCode();
    setIndex(index - 1);
  };

  const handleNext = () => {
    if (index >= total - 1 || submitting) return;
    rememberCurrentCode();
    setIndex(index + 1);
  };

  const handleRun = async () => {
    if (!question || isReviewLanguage(language)) return;
    setRunning(true);
    setExecutionBanner(null);
    const result = await runCode({
      questionId: question.question_id,
      language,
      code,
      stdin: customInput.trim() ? customInput : undefined,
    });
    setRunning(false);
    if (!result.ok) {
      if (result.rateLimited) {
        setExecutionBanner({ type: 'rate-limited', message: 'Could not run just now. Try again.' });
      } else if (result.unavailable) {
        setExecutionBanner({ type: 'unavailable', message: result.message });
      } else if (result.expired || result.alreadyComplete) {
        setExamComplete(true);
      }
      return;
    }
    setRunResults(result.results);
    setCompileError(result.compile_error);
  };

  const doSubmit = async () => {
    if (!question || submitLockRef.current) return;
    submitLockRef.current = true;
    setSubmitting(true);
    setExecutionBanner(null);
    try {
      const result = await submitCode({ questionId: question.question_id, language, code });
      setShowSubmitConfirm(false);
      if (!result.ok) {
        if (result.unavailable) setExecutionBanner({ type: 'unavailable', message: result.message });
        else if (result.expired || result.alreadyComplete) {
          setExamComplete(true);
          await stopRecording();
        }
        return;
      }
      if (result.next_index != null) {
        rememberCurrentCode();
        setIndex(result.next_index);
      } else {
        setExamComplete(true);
        await stopRecording();
      }
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  const handleAutoSubmitOnTimeout = async () => {
    // Always finish the session (not only the current question) so the server
    // scores every assigned problem and the report is generated.
    try {
      const payload = question
        ? { questionId: question.question_id, language, code }
        : {};
      await submitExam(payload);
    } catch (_) {
      /* still close the UI — server deadline / sweeper is the backstop */
    }
    setExamComplete(true);
    await stopRecording();
  };

  const doSubmitExam = async () => {
    if (submitLockRef.current || finishingRef.current) return;
    submitLockRef.current = true;
    finishingRef.current = true;
    setSubmitting(true);
    setExecutionBanner(null);
    try {
      const payload = question
        ? { questionId: question.question_id, language, code }
        : {};
      const result = await submitExam(payload);
      setShowSubmitExamConfirm(false);
      if (!result.ok) {
        if (result.expired || result.alreadyComplete) {
          setExamComplete(true);
          await stopRecording();
        } else {
          finishingRef.current = false;
        }
        return;
      }
      setExamComplete(true);
      await stopRecording();
    } catch (err) {
      finishingRef.current = false;
      setExecutionBanner({
        type: 'unavailable',
        message: 'Could not submit the exam right now. Please try again.',
      });
      setShowSubmitExamConfirm(false);
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  const securityBlocked = (!!fullscreenRequired && showFullscreenGate) || showScreenShareGate;

  if (examComplete) {
    return (
      <div className="coding-exam-room" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Coding exam complete</h2>
          <p>Thank you — your submissions have been recorded. You may close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="coding-exam-room">
      {!browserOnline && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 2000,
            background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D', borderRadius: 8,
            padding: '10px 16px', fontSize: 14, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,.12)',
            maxWidth: 'calc(100vw - 32px)', textAlign: 'center',
          }}
        >
          You are offline — reconnecting when your internet is back…
          <span style={{ fontWeight: 400 }}> Your work is saved. Please keep this tab open.</span>
        </div>
      )}
      {securityNotice && (
        <div className="coding-exam-security-toast">{securityNotice}</div>
      )}

      {fullscreenRequired && showFullscreenGate && (
        <div className="coding-exam-security-overlay">
          <div className="coding-exam-security-card">
            <h3>Fullscreen Required</h3>
            <p>
              This coding exam must run in fullscreen. If you exit fullscreen, you will be
              prompted to return immediately.
            </p>
            <button type="button" onClick={handleEnterFullscreen}>Enter Fullscreen</button>
          </div>
        </div>
      )}

      {!showFullscreenGate && showScreenShareGate && (
        <div className="coding-exam-security-overlay">
          <div className="coding-exam-security-card">
            <h3>Screen Share Required</h3>
            <p>
              Share your <strong>entire screen</strong> (not a window or browser tab) to continue.
              Stopping screen share pauses the exam until you share again.
            </p>
            <button type="button" onClick={handleRestoreScreenShare}>Share Entire Screen</button>
          </div>
        </div>
      )}

      <div className="coding-exam-header">
        <span className="question-progress">Question {index + 1} of {total}</span>
        <span className={`timer ${secondsLeft != null && secondsLeft <= WARNING_THRESHOLD_SECONDS ? 'warning' : ''}`}>
          {formatSeconds(secondsLeft)}
        </span>
        <span className="rec-indicator"><span className="rec-dot" /> REC</span>
      </div>

      <div className={`coding-exam-body${securityBlocked ? ' coding-exam-body-locked' : ''}`}>
        <div className="coding-exam-problem">
          <div className="coding-exam-problem-content">
            {loading && <p>Loading question…</p>}
            {loadError && <p style={{ color: '#f87171' }}>{loadError}</p>}
            {question && (
              <CodingProblemPanel question={question} questionNumber={index + 1} language={language} />
            )}
          </div>

          <div className="coding-exam-selfview">
            <ProctoringMonitor
              sessionId={sessionId}
              recordingStartTime={recordingStartTime}
              showCameraOnly={true}
              onVideoReady={handleVideoReady}
              videoRecorderRef={videoRecorderRef}
              prewarmedStream={prepResources?.stream}
              prewarmedReady={hasLivePrewarm}
              manageBrowserEvents={false}
              preserveStreamOnUnmount={false}
            />
          </div>
        </div>

        <div className="coding-exam-editor-pane">
          <div className="coding-exam-editor-toolbar">
            <div className="coding-exam-toolbar-left">
              <select
                value={language || ''}
                onChange={(e) => handleLanguageChange(e.target.value)}
                disabled={securityBlocked}
              >
                {(question?.languages || []).map((l) => <option key={l} value={l}>{codingLanguageLabel(l)}</option>)}
              </select>
              {total > 1 && (
                <span className="coding-exam-nav">
                  <button
                    type="button"
                    className="coding-exam-nav-btn"
                    disabled={securityBlocked || index <= 0 || submitting || loading}
                    onClick={handleBack}
                    title="Previous question"
                    aria-label="Previous question"
                  >
                    <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
                      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.7" />
                      <path d="M13.6 8.2 L9.4 12 L13.6 15.8" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="coding-exam-nav-btn"
                    disabled={securityBlocked || index >= total - 1 || submitting || loading}
                    onClick={handleNext}
                    title="Next question"
                    aria-label="Next question"
                  >
                    <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
                      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.7" />
                      <path d="M10.4 8.2 L14.6 12 L10.4 15.8" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </span>
              )}
              {isReviewLanguage(language) && (
                <span style={{ fontSize: 12, color: '#64748b' }}>Reviewed on submit. Run is not used for this question.</span>
              )}
            </div>
            <div className="coding-exam-actions">
              <button
                type="button"
                className="run-btn coding-exam-icon-btn"
                disabled={securityBlocked || !question}
                onClick={() => setShowResetConfirm(true)}
                title="Reset code"
                aria-label="Reset code"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path fill="currentColor" d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 7.73 10h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                </svg>
              </button>
              {!isReviewLanguage(language) && (
                <button
                  className="run-btn"
                  disabled={securityBlocked || running || !question}
                  onClick={handleRun}
                >
                  {running ? 'Running…' : 'Run'}
                </button>
              )}
              <button
                className="submit-btn"
                disabled={securityBlocked || submitting || !question}
                onClick={() => setShowSubmitConfirm(true)}
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
              <button
                type="button"
                className="submit-exam-btn"
                disabled={securityBlocked || submitting}
                onClick={() => setShowSubmitExamConfirm(true)}
              >
                {submitting ? 'Submitting…' : 'Submit Exam'}
              </button>
            </div>
          </div>

          <div className="coding-exam-editor-wrap">
            <Editor
              height="100%"
              theme="vs-dark"
              language={MONACO_LANGUAGE[language] || 'python'}
              value={code}
              onChange={handleCodeChange}
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                automaticLayout: true,
                readOnly: securityBlocked,
              }}
            />
          </div>

          {executionBanner && (
            <div className={`coding-exam-banner ${executionBanner.type}`}>{executionBanner.message}</div>
          )}

          {!isReviewLanguage(language) && (
            <textarea
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              placeholder="Custom input (optional). Leave empty to run the sample tests."
              disabled={securityBlocked}
              style={{
                width: '100%', minHeight: 64, resize: 'vertical', boxSizing: 'border-box',
                fontFamily: 'ui-monospace, monospace', fontSize: 12, padding: 8,
                border: '1px solid #334155', borderRadius: 8, background: '#0f172a', color: '#e2e8f0',
              }}
            />
          )}

          <div className="coding-exam-results">
            {compileError && <div className="coding-exam-compile-error">{compileError}</div>}
            {runResults && runResults.map((r) => (
              <div key={r.test_id} className={`coding-exam-test-row ${r.passed ? 'pass' : 'fail'}`}>
                <span>{r.passed ? '✓' : '✗'}</span>
                <div>
                  <div>{r.custom ? 'Custom input' : `Test ${r.passed ? 'passed' : 'failed'}`} ({r.runtime_ms}ms)</div>
                  {r.custom ? (
                    <div style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>{r.output}</div>
                  ) : !r.passed && (
                    <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      Expected: {r.expected}{'\n'}Actual: {r.output}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {!runResults && !compileError && (
              <p style={{ color: '#64748b', fontSize: 13 }}>
                {isReviewLanguage(language) ? 'Submit your component when it is ready. An interviewer reviews it.' : 'Run your code to see test results here.'}
              </p>
            )}
          </div>
        </div>
      </div>

      {showResetConfirm && !securityBlocked && (
        <div className="coding-exam-confirm-overlay">
          <div className="coding-exam-confirm-box">
            <h3>Reset starter code?</h3>
            <p>This replaces the code in the editor with the starter code for this question.</p>
            <div className="actions">
              <button type="button" onClick={() => setShowResetConfirm(false)} style={{ background: '#334155', color: '#e2e8f0' }}>Cancel</button>
              <button type="button" onClick={() => { setShowResetConfirm(false); handleReset(); }} style={{ background: '#0041C2', color: 'white' }}>Reset</button>
            </div>
          </div>
        </div>
      )}

      {showSubmitConfirm && !securityBlocked && (
        <div className="coding-exam-confirm-overlay">
          <div className="coding-exam-confirm-box">
            <h3>Submit this question?</h3>
            <p>
              {index >= total - 1
                ? 'Your answer is saved and this exam will finish.'
                : 'Your answer is saved. Use Back to return to this question before the exam ends.'}
            </p>
            <div className="actions">
              <button onClick={() => setShowSubmitConfirm(false)} disabled={submitting} style={{ background: '#334155', color: '#e2e8f0' }}>Cancel</button>
              <button onClick={doSubmit} disabled={submitting} style={{ background: '#16a34a', color: 'white' }}>{submitting ? 'Submitting…' : 'Submit'}</button>
            </div>
          </div>
        </div>
      )}

      {showSubmitExamConfirm && !securityBlocked && (
        <div className="coding-exam-confirm-overlay">
          <div className="coding-exam-confirm-box">
            <h3>Submit the entire exam?</h3>
            <p>
              This ends the coding exam now. Unattempted questions will be marked as not attempted.
              You cannot return after submitting.
            </p>
            <div className="actions">
              <button
                type="button"
                onClick={() => setShowSubmitExamConfirm(false)}
                disabled={submitting}
                style={{ background: '#334155', color: '#e2e8f0' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doSubmitExam}
                disabled={submitting}
                style={{ background: '#b45309', color: 'white' }}
              >
                {submitting ? 'Submitting…' : 'Submit Exam'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CodingExamRoom;
